import PPUtils from "../PPUtils"

const AI_MATCH_TYPE = "AI-Match"

const SCORING_EXCLUDED_DIMENSIONS = new Set(["ProductNr", "productNr", "Productnr"])

const MATCHING_JSON_SKIP_KEYS = new Set([
    "dimensionInfos",
    "MatchType",
    "matchType",
    "customerProductAttributes",
    "foreignShopProductAttributes",
    "staffelInfo",
    "openaiReason",
    "productAttributes"
])

export const LEGACY_ALGORITHM_MATCH_TYPE = "__ALGORITHM__"

export function buildFilterInput(shopId, producerId, minScore, matchType) {
    const hasShopId = PPUtils.isPositiveIntId(shopId)
    const hasProducerId = PPUtils.isPositiveIntId(producerId)
    const hasMinScore = typeof minScore === "number" && Number.isFinite(minScore) && minScore > 0
    const hasMatchType = typeof matchType === "string" && matchType !== "" && matchType !== "ALL"

    return {
        direction: "CUSTOMER_IS_MASTER",
        shopId: hasShopId ? shopId : null,
        producerId: hasProducerId ? producerId : null,
        minScore: hasMinScore ? minScore : null,
        matchType: hasMatchType ? matchType : null
    }
}

export function parseMatchingDetails(json) {
    if (!json) {
        return []
    }
    try {
        const data = typeof json === "string" ? JSON.parse(json) : json
        const infos = data?.dimensionInfos
        if (!infos || typeof infos !== "object") {
            return []
        }
        return Object.entries(infos).map(([name, info]) => ({
            name,
            score: info?.score ?? 0,
            weight: info?.weight ?? 0,
            customerShopValue: formatValue(info?.customerShopValue),
            foreignShopValue: formatValue(info?.foreignShopValue)
        }))
    } catch {
        return []
    }
}

function formatValue(value) {
    if (value === null || value === undefined) {
        return ""
    }
    if (typeof value === "object") {
        return JSON.stringify(value)
    }
    return String(value)
}

export function parseScoringDimensions(json) {
    return parseMatchingDetails(json).filter((dim) => !SCORING_EXCLUDED_DIMENSIONS.has(dim.name))
}

function attributeMapToList(attributes) {
    if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) {
        return []
    }
    return Object.entries(attributes)
        .map(([name, value]) => ({
            name,
            value: formatValue(value).trim()
        }))
        .filter((entry) => entry.value)
        .sort((a, b) => a.name.localeCompare(b.name, "de"))
}

export function parseMatchingProductAttributes(json) {
    if (!json) {
        return {
            customerAttributes: [],
            foreignAttributes: [],
            openaiReason: "",
            staffelInfo: null
        }
    }
    try {
        const data = typeof json === "string" ? JSON.parse(json) : json
        const openaiReason = String(data?.openaiReason || "").trim()
        const staffelInfo =
            data?.staffelInfo && typeof data.staffelInfo === "object" ? data.staffelInfo : null

        return {
            customerAttributes: attributeMapToList(data?.customerProductAttributes),
            foreignAttributes: attributeMapToList(data?.foreignShopProductAttributes),
            openaiReason,
            staffelInfo
        }
    } catch {
        return {
            customerAttributes: [],
            foreignAttributes: [],
            openaiReason: "",
            staffelInfo: null
        }
    }
}

function parseJsonAttributeDetails(json) {
    if (!json) {
        return []
    }
    try {
        const data = typeof json === "string" ? JSON.parse(json) : json
        const items = []

        for (const [key, value] of Object.entries(data)) {
            if (MATCHING_JSON_SKIP_KEYS.has(key)) {
                continue
            }
            if (key === "productAttributes" && value && typeof value === "object") {
                for (const [attrKey, attrValue] of Object.entries(value)) {
                    items.push({ key: `attr_${attrKey}`, label: attrKey, value: formatValue(attrValue) })
                }
                continue
            }
            if (value !== null && typeof value === "object") {
                continue
            }
            items.push({ key, label: key, value: formatValue(value) })
        }
        return items
    } catch {
        return []
    }
}

export function getAdditionalProductDetails(product, detailsJson) {
    if (!product) {
        return []
    }

    const items = []
    const seen = new Set()

    const add = (key, label, value) => {
        const normalized = value === null || value === undefined ? "" : String(value).trim()
        if (!normalized || seen.has(key)) {
            return
        }
        seen.add(key)
        items.push({ key, label, value: normalized })
    }

    if (product.modelName) {
        add("modelName", "ModelName", product.modelName)
    }
    if (product.color) {
        add("color", "Farbe", product.color)
    }
    if (product.extArtnr) {
        add("extArtnr", "EAN", product.extArtnr)
    }
    if (product.stock !== null && product.stock !== undefined) {
        add("stock", "Bestand", product.stock)
    }
    if (product.customerProductNr && product.customerProductNr !== product.productNr) {
        add("customerProductNr", "Kunden-Art.-Nr.", product.customerProductNr)
    }

    for (const { key, value } of getVisibleMatchingInfo(product.matchingInfo)) {
        if (key === "ProductNr" || key === "ModelName") {
            continue
        }
        add(`info_${key}`, key, value)
    }

    for (const { key, label, value } of parseJsonAttributeDetails(detailsJson)) {
        add(`json_${key}`, label, value)
    }

    return items
}

export function getMatchType(product) {
    const json = product?.aiMatchingDetailsJson || product?.matchingDetailsJson
    if (!json) {
        return null
    }
    try {
        const data = typeof json === "string" ? JSON.parse(json) : json
        return data?.MatchType || data?.matchType || (data?.dimensionInfos?.AIMatch ? "AI-Match" : null)
    } catch {
        return null
    }
}

export function isAiMatch(product) {
    const matchType = getMatchType(product)
    if (matchType) {
        return String(matchType).toLowerCase().includes("ai")
    }
    const json = product?.aiMatchingDetailsJson || product?.matchingDetailsJson
    if (!json) {
        return false
    }
    try {
        const data = typeof json === "string" ? JSON.parse(json) : json
        return !!data?.dimensionInfos?.AIMatch
    } catch {
        return false
    }
}

export function getMatchSourceLabel(product, algorithmLabel = "Algorithmus") {
    const matchType = getMatchType(product)
    if (matchType) {
        return matchType
    }
    return isAiMatch(product) ? "KI" : algorithmLabel
}

export function isKiMatchType(matchType) {
    return !!matchType && String(matchType).toLowerCase().includes("ai")
}

export function getDisplayName(product, includeColor = false) {
    let name = product?.name || ""
    if (includeColor && product?.color) {
        name += " - " + product.color
    }
    if (name.endsWith("- #")) {
        name = name.replace("- #", "")
    }
    return name
}

const URL_PREFIX_ENDING = /[=?&_-]$/

function endsWithProductNr(url, productNr) {
    if (!url || !productNr) {
        return false
    }
    const comparable = String(url).replace(/\/+$/, "")
    return comparable.toLowerCase().endsWith(String(productNr).toLowerCase())
}

function appendProductNrToPrefix(base, productNr) {
    if (!base) {
        return null
    }
    if (!productNr || endsWithProductNr(base, productNr) || !URL_PREFIX_ENDING.test(base)) {
        return base
    }
    return `${base}${productNr}`
}

export function resolveProductUrl(product, priceLang = "default") {
    if (!product) {
        return null
    }

    const productNr = product.productNr ?? product.customerProductNr ?? product.productnr
    const normalizedLang = (priceLang || "default").toLowerCase()

    if (product.urls && typeof product.urls === "object") {
        const fromUrls = product.urls[normalizedLang] ?? product.urls.de
        if (fromUrls) {
            return fromUrls
        }
    }

    const productUrl = product.productUrl
    if (productUrl) {
        const trimmed = String(productUrl).trim()
        if (PPUtils.isJSON(trimmed)) {
            try {
                const bases = JSON.parse(trimmed)
                const base = bases[normalizedLang] ?? bases.default
                return appendProductNrToPrefix(base, productNr)
            } catch {
                return null
            }
        }
        return appendProductNrToPrefix(trimmed, productNr)
    }

    const auth = PPUtils.getLogin()
    if (auth?.customer && productNr) {
        return (
            PPUtils.getCustomerProductURL(
                auth.customer,
                { productnr: productNr, urls: product.urls },
                normalizedLang
            ) || null
        )
    }

    return null
}

export function getVisibleMatchingInfo(matchingInfo) {
    if (!matchingInfo || typeof matchingInfo !== "object") {
        return []
    }
    return Object.entries(matchingInfo)
        .filter(([key]) => key !== "ModelName")
        .map(([key, value]) => ({ key, value: value ?? "" }))
}

export function filterCandidatesByMinScore(candidates, minScore) {
    if (!Array.isArray(candidates)) {
        return []
    }
    const hasMinScore = typeof minScore === "number" && Number.isFinite(minScore) && minScore > 0
    if (!hasMinScore) {
        return candidates
    }
    return candidates.filter((candidate) => {
        return candidate != null && typeof candidate.score === "number" && candidate.score >= minScore
    })
}

export function getBestOpenCandidate(candidates) {
    if (!Array.isArray(candidates) || candidates.length === 0) {
        return null
    }
    const open = candidates.filter((candidate) => candidate && !candidate.approved && !candidate.rejected)
    if (open.length === 0) {
        return null
    }
    return [...open].sort((a, b) => b.score - a.score)[0]
}

export function formatPrice(price, currencyId) {
    if (price === null || price === undefined) {
        return "—"
    }
    const num = Number(price)
    if (Number.isNaN(num)) {
        return String(price)
    }
    return num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
}

export { AI_MATCH_TYPE }
